/**
 * Odbory zákazky v hlavičke (KOLO 240, návrh H1b „Lišta odborov“).
 *
 * Zákazka má tri odbory — **Nábytok** (výpočty, cena pre zákazníka),
 * **Tovar** (rozpočty regálov CRC) a **Výroba** (výrobné varianty a časti).
 * Lišta vyberá odbor, kapsule pod ňou verziu v ňom. Varianta patrí práve
 * jednému odboru: tovar podľa `odbor`, výroba podľa `je_vyrobna`.
 */
import type { Varianta, ZakazkaDetail } from "@/api/schemas";
import { sklonuj } from "@/lib/format";
import { jeTovarovaVarianta, poradieVariant } from "@/lib/variantyZakazky";

export type OdborZakazky = "nabytok" | "tovar" | "vyroba";

export const ODBORY: readonly OdborZakazky[] = ["nabytok", "tovar", "vyroba"];

type PoleOdboru = Pick<Varianta, "je_vyrobna"> & { odbor?: Varianta["odbor"] };

export function odborVarianty(v: PoleOdboru): OdborZakazky {
  if (jeTovarovaVarianta(v)) return "tovar";
  return v.je_vyrobna ? "vyroba" : "nabytok";
}

/** Verzie odboru v poradí záložiek (výpočty v poradí vzniku). */
export function variantyOdboru<T extends PoleOdboru>(
  varianty: readonly T[],
  odbor: OdborZakazky,
): T[] {
  return poradieVariant(varianty).filter((v) => odborVarianty(v) === odbor);
}

/**
 * Je verzia v cene zákazky (★)? Výpočet = aktívny výpočet, tovar = aktívny
 * tovar, výroba = keď zákazka výpočet nemá a cena je súčet výroby (KOLO 208).
 */
export function jeVCene(
  zakazka: Pick<ZakazkaDetail, "aktivna_varianta_id" | "aktivna_tovar_varianta_id" | "cena_z_vyroby">,
  v: PoleOdboru & { id: string },
): boolean {
  switch (odborVarianty(v)) {
    case "tovar":
      return zakazka.aktivna_tovar_varianta_id === v.id;
    case "vyroba":
      return Boolean(zakazka.cena_z_vyroby);
    default:
      return zakazka.aktivna_varianta_id === v.id;
  }
}

/**
 * Kam ide klik na odbor v lište: naposledy otvorená verzia odboru (kým
 * existuje), inak tá v cene, inak prvá.
 */
export function cielOdboru(
  zakazka: Pick<ZakazkaDetail, "aktivna_varianta_id" | "aktivna_tovar_varianta_id" | "cena_z_vyroby">,
  varianty: readonly (PoleOdboru & { id: string })[],
  odbor: OdborZakazky,
  zapamatana: string | null,
): string | null {
  const vOdbore = variantyOdboru(varianty, odbor);
  if (zapamatana && vOdbore.some((v) => v.id === zapamatana)) return zapamatana;
  return (vOdbore.find((v) => jeVCene(zakazka, v)) ?? vOdbore[0])?.id ?? null;
}

/** „3 výpočty", „1 rozpočet", „2 časti" / „výroba" — popis pod odborom. */
export function popisOdboru(odbor: OdborZakazky, pocet: number): string {
  switch (odbor) {
    case "tovar":
      return `${pocet} ${sklonuj(pocet, ["rozpočet", "rozpočty", "rozpočtov"])}`;
    case "vyroba":
      return pocet >= 2 ? `${pocet} ${sklonuj(pocet, ["časť", "časti", "častí"])}` : "výroba";
    default:
      return `${pocet} ${sklonuj(pocet, ["výpočet", "výpočty", "výpočtov"])}`;
  }
}

export const NAZOV_ODBORU: Record<OdborZakazky, string> = {
  nabytok: "Nábytok",
  tovar: "Tovar",
  vyroba: "Výroba",
};
